// @ts-nocheck
/**
 * DetailsList — a fully props-driven key-value row list.
 *
 * Usage:
 *   <DetailsList
 *     title="Details"
 *     titleIcon={<LuHash className="w-3.5 h-3.5" />}
 *     items={[
 *       { label: "Action ID",  value: log.actionId,  mono: true },
 *       { label: "Status",     value: <PillCell ... /> },
 *       { label: "Duration",   value: et, hidden: !et },
 *     ]}
 *   />
 */
import React from 'react';
import classNames from 'classnames';
import { APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DetailsListItem {
  /** Row label (left side) */
  label: string;
  /** Row value (right side) — any ReactNode */
  value: React.ReactNode;
  /** Render value in a monospace style */
  mono?: boolean;
  /**
   * When true the row is completely omitted from rendering.
   * Useful for conditionally showing rows without ternary-littering the array.
   */
  hidden?: boolean;
  /** Custom className added to the value span */
  valueClassName?: string;
  /** Custom className added to the whole row */
  rowClassName?: string;
  /** Optional icon shown to the left of the label */
  labelIcon?: React.ReactNode;
}

export interface DetailsListProps {
  items: DetailsListItem[];
  /** Optional section heading */
  title?: React.ReactNode;
  /** Icon shown next to the title */
  titleIcon?: React.ReactNode;
  /** Extra className on the root wrapper */
  className?: string;
  /** Extra className on each row wrapper */
  rowClassName?: string;
  /** Show a top-border divider before the first row  (default: false) */
  topDivider?: boolean;
  /** Test id attribute */
  testId?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const DetailsList: React.FC<DetailsListProps> = ({
  items,
  title,
  titleIcon,
  className,
  rowClassName,
  topDivider = false,
  testId,
}) => {
  const visible = items.filter((r) => !r.hidden);

  return (
    <div
      className={classNames('flex flex-col gap-0', className)}
      data-testid={testId ?? ''}
    >
      {/* Optional section title */}
      {title && (
        <p
          className={classNames(
            'text-[10px] uppercase tracking-[0.14em] font-bold flex items-center gap-1.5 mb-2',
            APP_TEXT.subtle,
          )}
        >
          {titleIcon}
          {title}
        </p>
      )}

      {/* Rows */}
      <div
        className={classNames(
          'flex flex-col gap-0',
          topDivider && `border-t ${APP_BORDER.soft}`,
        )}
      >
        {visible.map((row, i) => (
          <div
            key={i}
            className={classNames(
              'flex items-center justify-between gap-3 py-2',
              i < visible.length - 1 && `border-b ${APP_BORDER.soft}`,
              row.rowClassName,
              rowClassName,
            )}
          >
            {/* Label */}
            <span
              className={classNames(
                'text-[10px] uppercase tracking-wider font-bold shrink-0 flex items-center gap-1',
                APP_TEXT.subtle,
              )}
            >
              {row.labelIcon && (
                <span className="opacity-60 [&>*]:w-3 [&>*]:h-3">
                  {row.labelIcon}
                </span>
              )}
              {row.label}
            </span>

            {/* Value */}
            {typeof row.value === 'string' || typeof row.value === 'number' ? (
              <span
                className={classNames(
                  'text-right',
                  row.mono
                    ? `text-[10px] font-mono break-all ${APP_TEXT.soft}`
                    : `text-[11px] ${APP_TEXT.soft}`,
                  row.valueClassName,
                )}
                data-testid={tid(`${row?.label}-details`)}
              >
                {row.value}
              </span>
            ) : (
              <span
                className={classNames(
                  'flex items-center justify-end',
                  row.valueClassName,
                )}
              >
                {row.value}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DetailsList;
export { DetailsList };
